import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import type { Property } from '../../../../core/models/property.model';
import { FormControl } from '@angular/forms';
import { combineLatest, Subject, Subscription } from 'rxjs';
import type { Entity } from '../../../../core/models/entity.model';
import { Store } from '@ngrx/store';
import { selectProjectFeedbackMessages } from '../../../store/project-data/project-data.selectors';
import { createNumericControlValidatorsForProperty } from '../../../../entity-editor/utils/validation-helpers';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-price-table-number-input',
  templateUrl: './price-table-number-input.component.html',
  styleUrls: ['./price-table-number-input.component.scss'],
  standalone: false
})
export class PriceTableNumberInputComponent implements OnInit, OnChanges, OnDestroy {
  @Input() property: Property;
  @Input() productId: string;
  @Input() rightAlign: boolean;
  @Input() languageCode: string;
  @Output() changed: EventEmitter<number>;

  protected control: FormControl<number>;
  protected messages: Entity[] = [];

  private propertyKeySubject = new Subject<string>();

  private changeSubscription: Subscription;
  private messageSubscription: Subscription;

  constructor(private store: Store, private translate: TranslateService) {
    this.control = new FormControl(0, { updateOn: 'blur' });
    this.changed = new EventEmitter();

    this.messageSubscription = combineLatest([
      this.store.select(selectProjectFeedbackMessages),
      this.propertyKeySubject.asObservable()
    ]).subscribe(([messages, key]) => {
      this.messages = messages.filter(x => x.ParentId === this.productId && (x.Properties['PropertyKey'].Value as string) === key);
    });
  }

  ngOnInit(): void {
    this.control.setValue(this.property?.Value as number);
    this.changeSubscription = this.control.valueChanges.subscribe(value => {
      if (!this.control.invalid) {
        this.changed.emit(value);
      }
    });
  }

  ngOnDestroy(): void {
    this.changeSubscription?.unsubscribe();
    this.messageSubscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['property']?.currentValue) {
      const oldValue: Property = changes['property'].previousValue;
      const newValue: Property = changes['property'].currentValue;
      this.control.setValidators(createNumericControlValidatorsForProperty(this.property));
      if (oldValue?.Value !== newValue?.Value) {
        this.control.setValue(this.property.Value as number);
      }

      this.propertyKeySubject.next(this.property.Key);
    }
  }

  protected getValidationErrorText() {
    if (this.messages.length > 0) {
      return this.translate.instant(this.messages[0].Properties['TextId'].Value as string);
    }

    return '';
  }
}
